/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime} from './AdobeMetricsRuntime';
import {Level, Metric, MetricsState, Record, User, UserOptOutAllowList} from '@exc/metrics';

const OVERSIZE_MSG = 'Oversized metric';
const OVERSIZE_LIMIT = 2499999;

export interface Payload {
  addMetric: (metric: Metric, state: MetricsState) => void;
  addUser: (user: User, state: MetricsState) => void;
  appendPayloadCount: (reason: string) => void;
  body: {
    log: string[];
    metricsState: MetricsState;
    user: string[];
  };
  count: () => number;
  countUser: () => number;
  next: () => void;
  /**
   * Gateway limit 3 MB - max flush threshold 500kB - 1 byte.
   * This way we never flush a payload > 3MB.
   */
  oversizeLimit: number;
  size: () => number;
  toJSONString: () => string;
}

/**
 * Apply special handling where needed to convert objects to a simple
 * property bag.
 *
 * @param {any} o The object to be converted.
 * @returns {any} A simple bag or the original object if no conversion was
 * needed.
 */
function makePropertyBag(o: any): any {
  if (o instanceof Error) {
    const e = o as any;
    return {
      columnNumber: e.columnNumber,
      fileName: e.fileName,
      lineNumber: e.lineNumber,
      message: e.message,
      name: e.name,
      stack: e.stack,
    };
  } else {
    return o;
  }
}

function checkMetricSize(metric: Metric): string {
  let s = JSON.stringify(metric);
  if (s.length > OVERSIZE_LIMIT) {
    delete metric.data;
    metric.data = makePropertyBag(new Error(OVERSIZE_MSG));
    metric.data.payloadSize = s.length;
    metric.level = Level.ERROR;
    metric.message = OVERSIZE_MSG + (!metric.message ? '' : ' | Original message: ' + metric.message);
    s = JSON.stringify(metric);
  }
  return s;
}

export default (adobeMetrics: AdobeMetricsRuntime, win?: Window): Payload => {
  const {document, location} = win || {};
  const log: string[] = [];
  const user: string[] = [];
  let size = 0;

  const body = {
    log,
    metricsState: {payloadSequence: 1},
    user,
  };

  function next(): void {
    body.log = [];
    body.user = [];
    ++body.metricsState.payloadSequence;
    size = 0;
  }

  function finalizeRecord(record: Record, state: MetricsState): void {
    if (Array.isArray(record.data)) {
      record.data = record.data.map(makePropertyBag);
    } else if (record.data) {
      record.data = makePropertyBag(record.data);
    } else {
      delete record.data;
    }

    record.metricsState = Object.assign({}, state, {
      guid: adobeMetrics.guid,
      timeOrigin: adobeMetrics.timeOrigin,
    });

    record.metricsState.window = Object.assign(
      {},
      adobeMetrics.metricsState && adobeMetrics.metricsState.window,
      document ? {visibility: document.visibilityState} : undefined,
      location ? {location: {href: location.href, origin: location.origin}} : undefined,
    );

    if (adobeMetrics.runtime.options.timestamp) {
      record.timestamp = record.dateNow ? new Date(record.dateNow) : new Date();
    }
  }

  const optOutData = (user: User): void => {
    for (const key of Object.keys(user)) {
      if (!UserOptOutAllowList.includes(key)) {
        delete (user as any)[key];
      }
    }
  };

  const count = (): number => body.user.length + body.log.length;
  const countUser = (): number => body.user.length;
  const addUser = (user: User, state: MetricsState): void => {
    finalizeRecord(user, state);
    state.flags?.user?.optOut && optOutData(user);
    const s = JSON.stringify(user);
    body.user.push(s);
    size += s.length;
  };

  function addMetric(metric: Metric, state: MetricsState): void {
    if (document?.visibilityState === 'hidden') {
      if (metric.recordType === 'ResourceTiming') {
        return;
      }
    }
    finalizeRecord(metric, state);
    metric.offset = adobeMetrics.now() - (metric.metricsState?.pageState?.epoch || 0);
    const s = checkMetricSize(metric);
    body.log.push(s);
    size += s.length;
  }

  // Append the userCount and logCount records to Log table
  function appendPayloadCount(reason: string): void {
    const metric = {
      counter: body.log.length + body.user.length,
      data: {log: body.log.length, reason, user: body.user.length},
      dateNow: Date.now(),
      recordType: 'PayloadCount',
      user: {},
    };
    if (adobeMetrics.user?.groupId && adobeMetrics.user.sessionId && adobeMetrics.user.userId) {
      Object.assign(metric, {
        user: {
          groupId: adobeMetrics.user.groupId,
          sessionId: adobeMetrics.user.sessionId,
          userId: adobeMetrics.user.userId,
        },
      });
    }
    finalizeRecord(metric, {sdkVersion: adobeMetrics.runtime.version} as any);
    const s = JSON.stringify(metric);
    body.log.push(s);
    size += s.length;
  }

  function toJSONString(): string {
    Object.assign(body.metricsState, {runtimeVersion: adobeMetrics.runtime.version}, adobeMetrics.metricsState);
    return (
      '{"log":[' +
      body.log.join(',') +
      '],"metricsState":' +
      JSON.stringify(body.metricsState) +
      ',"user":[' +
      body.user.join(',') +
      ']}'
    );
  }

  return {
    addMetric,
    addUser,
    appendPayloadCount,
    body,
    count,
    countUser,
    next,
    // Gateway limit 3 MB - max flush threshold 500kB - 1 byte
    // This way we never flush a payload > 3MB
    oversizeLimit: OVERSIZE_LIMIT,
    size: (): number => size,
    toJSONString,
  };
};
